import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api, type CreateChangeInput, type CreateOrganizationInput, type CreateRequestInput, type Decision, type DepartmentInput, type EmployeeInput } from '@/data'
import type { BalanceRecord, CustomDay, OrgSettings } from '@/domain/vacations'
import { errorMessage } from '@/lib/errors'

export function useWorkspaceQuery(orgId: string | undefined) {
  return useQuery({
    queryKey: ['workspace', orgId],
    queryFn: () => api.workspace(orgId!),
    enabled: Boolean(orgId),
    staleTime: 30_000,
  })
}

export function useActivity(orgId: string) {
  return useQuery({ queryKey: ['activity', orgId], queryFn: () => api.activity(orgId), staleTime: 30_000 })
}

/** Mutation that refreshes the workspace and shows a toast (errors in clear Portuguese). */
function useOrgMutation<TVars, TResult>(orgId: string, fn: (vars: TVars) => Promise<TResult>, success?: string | ((r: TResult, v: TVars) => string)) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (r, v) => {
      void qc.invalidateQueries({ queryKey: ['workspace', orgId] })
      void qc.invalidateQueries({ queryKey: ['activity', orgId] })
      if (success) toast.success(typeof success === 'function' ? success(r, v) : success)
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
}

export const useCreateRequest = (orgId: string) =>
  useOrgMutation(orgId, (v: CreateRequestInput) => api.createRequest(orgId, v), 'Pedido enviado. O seu gestor vai ser notificado.')
export const useCancelRequest = (orgId: string) => useOrgMutation(orgId, (id: string) => api.cancelRequest(orgId, id), 'Pedido cancelado.')
export const useDecideRequest = (orgId: string) =>
  useOrgMutation(
    orgId,
    (v: { id: string; decision: Decision; comment?: string }) => api.decideRequest(orgId, v.id, v.decision, v.comment),
    (_r, v) => (v.decision === 'approve' ? 'Férias aprovadas.' : 'Pedido recusado.'),
  )
export const useCreateChange = (orgId: string) =>
  useOrgMutation(orgId, (v: CreateChangeInput) => api.createChange(orgId, v), 'Pedido enviado ao gestor.')
export const useDecideChange = (orgId: string) =>
  useOrgMutation(
    orgId,
    (v: { id: string; decision: Decision; comment?: string }) => api.decideChange(orgId, v.id, v.decision, v.comment),
    (_r, v) => (v.decision === 'approve' ? 'Alteração aprovada.' : 'Alteração recusada.'),
  )
export const useSaveEmployee = (orgId: string) => useOrgMutation(orgId, (v: EmployeeInput) => api.saveEmployee(orgId, v), 'Pessoa guardada.')
export const useSaveDepartment = (orgId: string) => useOrgMutation(orgId, (v: DepartmentInput) => api.saveDepartment(orgId, v), 'Departamento guardado.')
export const useSaveBalance = (orgId: string) => useOrgMutation(orgId, (v: BalanceRecord) => api.saveBalanceRecord(orgId, v), 'Saldo atualizado.')
export const useSaveCustomDay = (orgId: string) =>
  useOrgMutation(orgId, (v: Omit<CustomDay, 'id'> & { id?: string }) => api.saveCustomDay(orgId, v), 'Dia guardado no calendário.')
export const useDeleteCustomDay = (orgId: string) => useOrgMutation(orgId, (id: string) => api.deleteCustomDay(orgId, id), 'Dia removido.')
export const useUpdateSettings = (orgId: string) =>
  useOrgMutation(orgId, (v: { settings: OrgSettings; name?: string }) => api.updateSettings(orgId, v.settings, v.name), 'Regras guardadas.')

export function useCreateOrganization() {
  return useMutation({
    mutationFn: (v: CreateOrganizationInput) => api.createOrganization(v),
    onError: (e) => toast.error(errorMessage(e)),
  })
}
